import { useEffect, useRef, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { Loader2, Sparkles } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { inputBase } from '@/components/ui/input';
import { api, type ApiError } from '@/lib/api';
import type { GbpReviewDraftResponse } from '@/lib/types';
import { cn } from '@/lib/utils';

// S3 §4.2 flow: "Draft with AI" spends 1 AI help and ONLY writes a draft;
// "Approve & send" reaches Google; the textarea is always editable and no
// failure path clears it. Visuals per S3 screen-design addendum §A: filled-
// sage ("Generate fix" idiom), InspectorField's textarea/focus classes, a
// visible label, reassurance line above the button row, direct textarea
// focus on a landed draft (list-item expansion, not a body swap).

const MAX_REPLY = 4000;

// Server error code → plain sentence. Never render a raw code.
const REPLY_ERRORS: Record<string, string> = {
  quota_exceeded:
    "You've used all your AI helps for this month. You can still write and send a reply yourself, or add more helps from Billing.",
  compliance_refused:
    "We couldn't draft this one — the draft came out with wording Google doesn't allow in review replies. Try writing it yourself.",
  reply_rejected: "Google didn't accept this reply. Edit the text and try again.",
  reauth_needed:
    'Your Google connection needs to be renewed. Reconnect from the top of this page, then try again.',
  already_replied: 'This review already has a posted reply.',
  send_in_progress: 'That reply is already being sent — give it a second and refresh.',
  locked: "Your current plan doesn't include this anymore. Check your plan in Billing.",
  not_verified: "Google hasn't finished verifying this listing yet, so changes can't be published.",
  not_connected: "This site's Google connection was removed. Reconnect from the top of this page.",
  gbp_unavailable: "Google features aren't available right now. Try again later.",
};
const REPLY_ERROR_FALLBACK = "That didn't go through. Your text is saved here — try again in a moment.";
function replyErrorMessage(code: string | undefined): string {
  return (code && REPLY_ERRORS[code]) ?? REPLY_ERROR_FALLBACK;
}

// Server answers `{ error: '<code>' }` on failure — ApiError's `.body` is the
// parsed JSON, so read the code off it rather than the derived message.
function apiErrorCode(err: unknown): string | undefined {
  const body = (err as ApiError | undefined)?.body;
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error: unknown }).error)
    : undefined;
}

// T12 controller ruling R1: "Draft a reply with AI" sits on EVERY review row,
// so a filled primary here paints the list with primaries. The screen's one
// primary is the send/connect CTA (T8); a repeated list action is an outline.
function AiButton(props: {
  variant: 'outline';
  label: string;
  busy: 'draft' | 'send' | null;
  onClick: () => void;
}) {
  const { variant, label, busy, onClick } = props;
  return (
    <button
      type="button"
      disabled={busy !== null}
      onClick={onClick}
      className={buttonVariants({ size: 'sm', variant })}
    >
      {busy === 'draft' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
      )}
      {busy === 'draft' ? 'Drafting…' : label}
    </button>
  );
}

// T12/B2: the ONE input recipe (ui/input.tsx's `inputBase`) plus the
// textarea-only sizing. It used to be a hand-rolled copy that had drifted.
const textareaClasses = cn(inputBase, 'py-2 leading-relaxed resize-y min-h-[6rem]');

export function ReviewReplyBox(props: {
  siteId: string;
  reviewId: string;
  initialDraft: string | null;
  canReply: boolean;
  onChanged: (announcement?: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const { siteId, reviewId, initialDraft, canReply, onChanged } = props;
  const [open, setOpen] = useState(initialDraft !== null);
  const [text, setText] = useState(initialDraft ?? '');
  const [busy, setBusy] = useState<'draft' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Set right before a draft/open update; consumed once the DOM reflects it.
  // Never true on mount, so an already-open saved draft doesn't steal focus.
  const focusPending = useRef(false);

  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [open, text]);

  if (!canReply) {
    return (
      <p className="mt-3 text-xs text-text-tertiary">
        Reply to reviews from pageboss — with an AI draft — on the Business plan.
      </p>
    );
  }

  const errorId = `reply-${reviewId}-error`;
  const basePath = locationApi(businessId, siteId, `gbp/reviews/${encodeURIComponent(reviewId)}`);

  async function draftWithAi() {
    setBusy('draft');
    setError(null);
    try {
      const res = await api.post<GbpReviewDraftResponse>(`${basePath}/draft`, {});
      setText(res.text);
      setOpen(true);
      focusPending.current = true;
    } catch (err) {
      setError(replyErrorMessage(apiErrorCode(err)));
    } finally {
      setBusy(null);
    }
  }

  async function approveAndSend() {
    setBusy('send');
    setError(null);
    try {
      await api.post(`${basePath}/reply`, { text });
      onChanged('Reply posted to Google.');
    } catch (err) {
      setError(replyErrorMessage(apiErrorCode(err)));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      {open ? (
        <>
          <label
            className="eyebrow"
            htmlFor={`reply-${reviewId}`}
          >
            Your reply
          </label>
          <textarea
            ref={textareaRef}
            id={`reply-${reviewId}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX_REPLY}
            rows={4}
            aria-describedby={error ? errorId : undefined}
            className={textareaClasses}
          />
          <p className="text-xs text-text-tertiary">Nothing goes to Google until you send it.</p>
          {error && (
            <p id={errorId} className="text-sm text-danger-fg" role="alert">{error}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy !== null || text.trim().length === 0}
              onClick={() => void approveAndSend()}
              className={buttonVariants({ size: 'sm' })}
            >
              {busy === 'send' ? 'Sending…' : 'Approve & send'}
            </button>
            <AiButton variant="outline" label="Redraft with AI" busy={busy} onClick={() => void draftWithAi()} />
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <AiButton variant="outline" label="Draft a reply with AI" busy={busy} onClick={() => void draftWithAi()} />
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setOpen(true);
                focusPending.current = true;
              }}
              className={buttonVariants({ size: 'sm', variant: 'ghost' })}
            >
              Write it myself
            </button>
          </div>
          {error && <p className="text-sm text-danger-fg" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}
